import { RefObject, useLayoutEffect, useState } from 'react'
import { sameSize, Size } from './videoLayout'

/**
 * The element's content size in whole pixels, kept current with a ResizeObserver (window
 * `resize` where ResizeObserver is unavailable). State only changes when the size does, so a
 * component using it re-renders on real resizes and nothing else.
 */
const useElementSize = (ref: RefObject<HTMLElement>): Size | null => {
  const [size, setSize] = useState<Size | null>(null)

  // Layout effect: measure before the first paint so nothing is drawn at a wrong size.
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return undefined

    const update = () => {
      const next = { width: element.clientWidth, height: element.clientHeight }
      setSize((prev) => (sameSize(prev, next) ? prev : next))
    }
    update()

    // The library compiles against an older DOM lib without ResizeObserver typings.
    const ResizeObserverImpl = (window as any).ResizeObserver
    if (ResizeObserverImpl) {
      const observer = new ResizeObserverImpl(update)
      observer.observe(element)
      return () => observer.disconnect()
    }
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [ref])

  return size
}

export default useElementSize
